/**
 * Cookie Banner Component
 * Simple cookie consent banner with OK button
 * Premium iOS-style spring animation with accessibility support
 */

import CookieConsent from 'react-cookie-consent';
import { motion, AnimatePresence } from 'framer-motion';
import { getAppDomain } from '../../utils/domainUtils';

/**
 * Get domain-based cookie consent name
 * Each restaurant domain will have its own cookie consent
 */
export const getCookieConsentName = (): string => {
  const domain = getAppDomain();
  return `${domain}_cookie_consent`;
};

// Check for reduced motion preference (WCAG + Lighthouse requirement)
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface CookieBannerProps {
  onAccept?: () => void;
}

/**
 * Cookie consent banner with OK button and smooth spring animation
 * - Respects prefers-reduced-motion for accessibility
 * - Gold standard animation values (Apple/Stripe quality)
 * - CLS safe with willChange
 */
// eslint-disable-next-line react/prop-types
const CookieBanner: React.FC<CookieBannerProps> = ({ onAccept }) => {
  const cookieName = getCookieConsentName();

  return (
    <AnimatePresence>
      <motion.div
        lang="en"
        className="notranslate"
        translate="no"
        style={{
          willChange: 'transform',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 999999,
        }}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : {
                delay: 0.12,
                type: 'spring',
                stiffness: 85,
                damping: 20,
                mass: 0.8,
              }
        }
      >
        <CookieConsent
          location="bottom"
          buttonText="OK"
          cookieName={cookieName}
          expires={365}
          onAccept={onAccept}
          containerClasses="notranslate"
          style={{
            background: '#252627',
            color: '#ffffff',
            padding: '16px 80px',
            borderTop: '1px solid #252627',
            boxShadow: '0 -6px 24px rgba(0,0,0,0.12)',
          }}
          contentStyle={{
            flex: '1 0 300px',
            margin: '0',
            fontSize: '14px',
            lineHeight: '1.6',
          }}
          buttonStyle={{
            background: '#F54900',
            color: '#fff',
            fontSize: '14px',
            fontWeight: '600',
            padding: '10px 20px',
            borderRadius: '8px',
            margin: '0 8px',
            cursor: 'pointer',
            border: 'none',
          }}
          buttonWrapperClasses="flex items-center gap-2"
        >
          <span className="notranslate" translate="no">
            We use essential cookies and local storage to ensure this website
            works properly and provides you with the best browsing experience.
          </span>
        </CookieConsent>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;

/**
 * Helper function to check consent status
 */
export const hasAcceptedCookies = (): boolean => {
  const cookieName = getCookieConsentName();
  const cookies = document.cookie.split(';');
  return cookies.some(cookie => cookie.trim().startsWith(`${cookieName}=true`));
};

export const resetCookieConsent = (): void => {
  const cookieName = getCookieConsentName();
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  window.location.reload();
};
